import { useQueries } from '@tanstack/react-query';
import _ from 'lodash';
import { ReactNode, useRef, useState } from 'react';
import { queryClient } from '../../../../App';
import { IUseFilter, useFilterData, useFilterValue } from '../../../../components';
import * as services from '../../../../components/FilterSection/services';
import { getAuthors, getCampaignTypes, getStatus } from '../services';

export type IFilterItem = {
  key: string;
  queryFn?: (query: string) => Promise<Record<'label' | 'value', string>[]>;
  selectAll?: boolean;
  type?: 'check' | 'select';
  placeholder?: ReactNode;
  textSearch?: string;
  textLoading?: string;
} & Record<string, unknown>;

export type IUseFilterOptions = {
  filters: IFilterItem[];
};

type IDataItem = {
  label: string;
  value: string;
};

const options: IUseFilterOptions = {
  filters: [
    {
      key: 'productIds',
      placeholder: 'Produto',
      queryFn: services.listProducts,
      textLoading: 'Carregando produtos...',
    },
    {
      key: 'storeIds',
      placeholder: 'Loja',
      queryFn: services.listStores,
      selectAll: true,
      textLoading: 'Carregando lojas...',
    },
    {
      key: 'clusters',
      placeholder: 'Cluster',
      queryFn: services.listClusters,
      selectAll: false,
      textLoading: 'Carregando clusters...',
    },
    {
      key: 'campaigns',
      placeholder: 'Tipo de Campanha',
      queryFn: getCampaignTypes,
      selectAll: false,
      textLoading: 'Carregando campanhas...',
      type: 'select',
      searchable: false,
    },
    {
      key: 'status',
      placeholder: 'Status',
      queryFn: getStatus,
      selectAll: false,
      textLoading: 'Carregando status...',
      type: 'select',
      searchable: false,
    },
    {
      key: 'responsibles',
      placeholder: 'Responsável',
      queryFn: getAuthors,
      selectAll: false,
      textLoading: 'Carregando responsáveis...',
      type: 'select',
      searchable: false,
    },
  ],
};

const queryKey = 'get-filter-data-campaign-';

export const useCampaignFilters = () => {
  const [data, { set: setData }] = useFilterData();

  const [cache, { set: setCache }] = useFilterData();

  const [values, { set: setValues, reset: resetValues, resetByKey: resetValuesByKey }] = useFilterValue();

  const [search, setSearch] = useState<Partial<Record<string, string>>>({});

  const timer = useRef<NodeJS.Timeout>();

  const onSearch = (key: string, query: string) => {
    clearTimeout(timer.current);
    const newTimer = setTimeout(() => {
      setSearch(oldValue => ({ ...oldValue, [key]: query }));
    }, 500);
    timer.current = newTimer;
  };

  useQueries({
    queries: options.filters.map(({ key, queryFn }) => ({
      queryKey: [queryKey + key, search[key]],
      queryFn: () => queryFn?.(search[key] ?? ''),
      onSuccess: (data: IDataItem[]) => {
        setData(key, _.unionBy([...(cache[key] ?? []), ...(data ?? [])], 'value'));
      },
      retry: false,
    })),
  });

  const onSelect = (key: string, value: string[]) => {
    setValues(key, value);
    const updatedList = data[key]?.filter(item => value.includes(item.value)) ?? [];
    setCache(key, updatedList);
  };

  const onClearAll = () => {
    resetValues();
  };

  const onSelectAll = (key: string, checked: boolean) => {
    if (!checked) {
      return resetValuesByKey(key);
    }
    const allData = data[key]?.map(({ value }) => value) ?? [];
    return setValues(key, allData);
  };

  const isEmpty = (() => {
    let isEmpty = true;
    Object.values(values).forEach(v => {
      if (Array.isArray(v) && v.length > 0) {
        isEmpty = false;
      }
    });
    return isEmpty;
  })();

  const loading = (() => {
    const loadingObject: IUseFilter['loading'] = {};
    Object.keys(data).forEach(key => {
      loadingObject[key] = !!queryClient.isFetching({ queryKey: [queryKey + key, search[key]] });
    });
    return loadingObject;
  })();

  return {
    states: {
      filters: options.filters,
      data,
      values,
      isEmpty,
      loading,
    },
    handlers: {
      onSearch,
      onSelect,
      onClearAll,
      onSelectAll,
    },
  };
};
